<script lang="ts">
  import type {CinemaSponsoredProduct} from "../../types/CinemaProductsDto.types.ts";
  import ProductImage from "./product-image/ProductImage.svelte";
  import Brand from "./brand/Brand.svelte";
  import Title from "./title/Title.svelte";
  import PriceSection from "./price/PriceSection.svelte";
  import OfcPriceComponent from "./price/OfcPriceComponent.svelte";
  import DiscountTag from "./sales-tags/DiscountTag.svelte";
  import BenefitTag from "./sales-tags/BenefitTag.svelte";
  import ProductBadge from "./product-badge/ProductBadge.svelte";
  import OttoUp from "./otto-up/OttoUp.svelte";
  import EekLabel from "./energy-label/EekLabel.svelte";
  import type {TrackingLabels} from "../../lib/tracking/trackingUtils.ts";
  import {
    buildCinemaFeatureForClick,
    buildClickBaseLabels,
    buildTileFeatureForClick,
  } from "../../lib/tracking/trackingUtils.ts";
  import {getContext, onMount} from "svelte";
  import cinemaTilePromiseKey, {type AddPromise,} from "../../lib/tracking/cinemaTilePromiseKey";

  type CinemaProductProps = {
    variation: CinemaSponsoredProduct;
    lazyLoad: boolean;
    auctionId: string;
    position: number;
    cinemaTrackingLabels: TrackingLabels;
    useOfcPricingItem?: boolean;
    useOfcWishlistSaveButton?: boolean;
  };

  const addPromise = getContext<AddPromise | undefined>(cinemaTilePromiseKey);
  if (typeof addPromise === "function") {
    addPromise(
      new Promise<void>((resolve) => {
        onMount(() => resolve());
      }),
    );
  }

  const {
    variation,
    lazyLoad,
    auctionId,
    position,
    cinemaTrackingLabels,
    useOfcPricingItem,
    useOfcWishlistSaveButton,
  }: CinemaProductProps = $props();

  const clickFeatures = $derived([
    buildCinemaFeatureForClick(auctionId, cinemaTrackingLabels),
    buildTileFeatureForClick(
      auctionId,
      variation.variationId,
      position,
      variation.tileTrackingLabels as TrackingLabels,
    ),
  ]);

  const clickBaseLabels = $derived(buildClickBaseLabels(clickFeatures));

  const showOfcPricingItem = $derived(
    useOfcPricingItem && variation.priceForOfcPricingItem?.retailPrice != null,
  );
</script>

<div>
  <div class="tile-content">
    <oc-link-v2
      base64-href={btoa(variation.detailPageLink)}
      oc-aria-label={`${variation.brand} – ${variation.title}`}
    >
      <a
        class="detail-page-url"
        href={variation.canonicalLink}
        data-tr-v1={JSON.stringify(clickBaseLabels)}
        data-tr-v1.track="click,auxclick"
        data-tr-v1.method="submitMove"
        data-tr-v1.action={JSON.stringify({
          name: "click",
          features: clickFeatures,
        })}
      >
        <div class="image-container">
          <ProductImage image={variation.image} {lazyLoad}>
            <ProductBadge variationId={variation.variationId}/>
          </ProductImage>
        </div>
        <div>
          <Brand>{variation.brand}</Brand>
          <Title>{variation.title}</Title>
        </div>
        <div>
          {#if showOfcPricingItem}
            <OfcPriceComponent variationId={variation.variationId}
                               priceForOfcPricingItem={variation.priceForOfcPricingItem!}/>
          {:else}
            <PriceSection price={variation.price}></PriceSection>
          {/if}
        </div>
      </a>
    </oc-link-v2>
    {#if useOfcWishlistSaveButton}
      <div class="wishlist-save-button">
        <ofc-wishlist-save-button-v1
          variant="icon-button-transparent"
          variation-id={variation.variationId}
          qualified={!variation.multipleSizes}
          source="recoSpa"
        ></ofc-wishlist-save-button-v1>
      </div>
    {/if}
    {#if !useOfcPricingItem}
      <div class="tags">
        {#if variation.salesTags}
          <DiscountTag discount={variation.salesTags.discount}/>
        {/if}
        <BenefitTag variationId={variation.variationId}/>
      </div>
    {/if}
    {#if variation.ottoUp}
      <OttoUp increasedMarginTop={showOfcPricingItem}/>
    {/if}
    <div>
      {#if variation.energyLabels.length}
        <EekLabel
          labels={variation.energyLabels}
          increasedMarginTop={showOfcPricingItem && !variation.ottoUp}
        />
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .tile-content {
    background-color: tokens.$oc-base-color-gray-25;
    overflow: hidden;
    position: relative;
  }

  .image-container {
    overflow: hidden;
    border-radius: tokens.$oc-semantic-border-radius-200;
  }

  .wishlist-save-button {
    position: absolute;
    right: 6px;
    top: 6px;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: tokens.$oc-semantic-spacing-25;
    margin-top: tokens.$oc-semantic-spacing-25;
  }

  .detail-page-url {
    color: tokens.$oc-base-color-black-100;
  }

  .detail-page-url:hover {
    text-decoration: tokens.$oc-base-color-black-100 underline;
    /*                                                                                                                   */
    /*                                                                                                   */
    --tile-hover-decoration-none: #{tokens.$oc-base-color-gray-25} underline;
    --tile-hover-decoration-grey: #{tokens.$oc-semantic-color-text-secondary}
    underline;
  }
</style>
